import React, { useState } from 'react';
import { useMsal } from "@azure/msal-react";
import Header from './components/Header';
import DocumentUpload from './components/DocumentUpload';
import DocumentAssistant from './components/DocumentAssistant';
import ProcessingStatus from './components/ProcessingStatus';
import AzureFilesList from './components/AzureFilesList';
import Tabs from './components/Tabs';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';
import { loginRequest } from "./authConfig";
import { useApiWithAuth } from './hooks/useApiWithAuth';
import { API_BASE_URL } from './config';

function App() {
  const { instance, accounts } = useMsal();
  const [processingDocuments, setProcessingDocuments] = useState([]);
  const [processingLoading, setProcessingLoading] = useState(false);
  const { fetchWithAuth } = useApiWithAuth();

  /*eslint-disable*/
  const getAccessToken = async () => {
    if (accounts.length === 0) {
      await instance.loginRedirect(loginRequest);
    }
    const result = await instance.acquireTokenSilent({
      ...loginRequest,
      account: instance.getAllAccounts()[0],
    });
    return result.accessToken;
  };

  const pollProcessingStatus = async (processingId, docId) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/processing-status/${processingId}`);
      const result = await response.json();

      if (result.success) {
        const status = result.status;
        
        // Update document status
        setProcessingDocuments(prev => 
          prev.map(doc => 
            doc.id === docId 
              ? { 
                  ...doc, 
                  status: status.status,
                  progress: status.progress,
                  stage: status.stage,
                  message: status.message,
                  results: status.results,
                  error: status.error
                }
              : doc
          )
        );

        // Continue polling if not completed or error
        if (status.status !== 'completed' && status.status !== 'error') {
          setTimeout(() => pollProcessingStatus(processingId, docId), 2000); // Poll every 2 seconds
        }
      }
    } catch (error) {
      console.error('Error polling processing status:', error);
      // Stop polling on error and mark as error
      setProcessingDocuments(prev => 
        prev.map(doc => 
          doc.id === docId 
            ? { 
                ...doc, 
                status: 'error',
                progress: 0,
                error: 'Failed to get processing status'
              }
            : doc
        )
      );
    }
  };

  const handleDocumentUpload = async (documentInfo) => {
    // Add document to processing queue with initial status
    const processingDoc = {
      id: Date.now(),
      name: documentInfo.name,
      category: documentInfo.category,
      status: 'uploading',
      progress: 0,
      stage: 'Preparing upload',
      message: 'Preparing to upload document...',
      timestamp: new Date().toISOString()
    };
    
    setProcessingDocuments(prev => [...prev, processingDoc]);
    
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', documentInfo.file);
      formData.append('documentName', documentInfo.documentName);
      formData.append('category', documentInfo.category);
      formData.append('location', documentInfo.location);

      // Update status to uploading
      setProcessingDocuments(prev => 
        prev.map(doc => 
          doc.id === processingDoc.id 
            ? { ...doc, status: 'uploading', progress: 10, stage: 'Uploading to Azure', message: 'Uploading file to Azure inbound folder...' }
            : doc
        )
      );

      // Make API call to upload file
      const response = await fetchWithAuth(`${API_BASE_URL}/upload`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        // Update with upload success and file info
        setProcessingDocuments(prev => 
          prev.map(doc => 
            doc.id === processingDoc.id 
              ? { 
                  ...doc, 
                  status: 'uploaded', 
                  progress: 25,
                  stage: 'Upload complete',
                  message: 'File successfully uploaded to Azure',
                  fileInfo: result.file_info,
                  processingId: result.processing_id
                }
              : doc
          )
        );

        // Start polling for processing status if it's a PDF that will be processed
        if (result.file_info.will_process && result.processing_id) {
          setTimeout(() => pollProcessingStatus(result.processing_id, processingDoc.id), 1000);
        } else {
          // For non-PDF files, mark as completed
          setTimeout(() => {
            setProcessingDocuments(prev => 
              prev.map(doc => 
                doc.id === processingDoc.id 
                  ? { 
                      ...doc, 
                      status: 'completed', 
                      progress: 100,
                      stage: 'Upload complete',
                      message: 'File uploaded successfully (AI processing only available for PDF files)'
                    }
                  : doc
              )
            );
          }, 1000);
        }

      } else {
        // Handle upload error
        setProcessingDocuments(prev => 
          prev.map(doc => 
            doc.id === processingDoc.id 
              ? { 
                  ...doc, 
                  status: 'error', 
                  progress: 0,
                  stage: 'Upload failed',
                  error: result.message || 'Upload failed'
                }
              : doc
          )
        );
      }

    } catch (error) {
      console.error('Upload error:', error);
      
      // Handle network or other errors
      setProcessingDocuments(prev => 
        prev.map(doc => 
          doc.id === processingDoc.id 
            ? { 
                ...doc, 
                status: 'error', 
                progress: 0,
                stage: 'Network error',
                error: 'Network error: Could not connect to server'
              }
            : doc
        )
      );
    }
  };

  const handleStartProcessing = async (files) => {
    setProcessingLoading(true);
    try {
      // Make API call to start processing
      const response = await fetchWithAuth(`${API_BASE_URL}/process-inbound`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ files }),
      });

      const result = await response.json();

      if (result.success) {
        // Add each file to processing queue
        result.processed_files.forEach(({ file_name, processing_id }) => {
          const processingDoc = {
            id: processing_id,
            name: file_name,
            category: 'invoice', // Default category for inbound files
            status: 'processing',
            progress: 10,
            stage: 'Starting processing',
            message: 'Initiating document processing...',
            timestamp: new Date().toISOString()
          };
          
          setProcessingDocuments(prev => [...prev, processingDoc]);
          
          // Start polling for processing status
          pollProcessingStatus(processing_id, processing_id);
        });

        // Add skipped files with completed status
        if (result.skipped_files && result.skipped_files.length > 0) {
          result.skipped_files.forEach(file_name => {
            const skippedDoc = {
              id: `skipped_${Date.now()}_${file_name}`,
              name: file_name,
              category: 'invoice',
              status: 'completed',
              progress: 100,
              stage: 'Already processed',
              message: 'File was already processed and exists in outbound folder',
              timestamp: new Date().toISOString()
            };
            
            setProcessingDocuments(prev => [...prev, skippedDoc]);
          });
        }
      } else {
        // Handle error
        console.error('Error starting processing:', result.message);
      }
    } catch (error) {
      console.error('Network error:', error);
    } finally {
      setProcessingLoading(false);
    }
  };

  return (
    <ProtectedRoute>
      <div className="app">
        <Header />
        <main className="main-content">
          <div className="content-grid">
            <div className="upload-section">
              <Tabs
                tabs={[
                  { label: 'Auto Uploaded Docs', content: <AzureFilesList onStartProcessing={handleStartProcessing} processingLoading={processingLoading} /> },
                  { label: 'Document Upload', content: <DocumentUpload onDocumentUpload={handleDocumentUpload} /> },
                ]}
              />
              
              {processingDocuments.length > 0 && (
                <ProcessingStatus documents={processingDocuments} />
              )}
            </div>
            
            <div className="assistant-section">
              <DocumentAssistant />
            </div>
          </div>
        </main>
      </div>
    </ProtectedRoute>
  );
}

export default App;